import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Copy, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { useTableViewSort } from "../hooks/useTableViewSort";
import { useTableSearchHighlight, SearchCell } from "../hooks/useTableSearchHighlight";
import TableSortTh from "./TableSortTh";
import TableViewToolbar from "./TableViewToolbar";
import OSCard, { OSCardNotice, OS_TH_CLS, OS_PLAIN_DISABLED_CLS } from "./OSCard";
import { useToast } from "../context/ToastContext";
import type { RazmerCopySource } from "../utils/rnMaterialnoCopySource";
import RNRazmerCellSaveMark from "./RNRazmerCellSaveMark";
import { useRNQueuedCellSave } from "../hooks/useRNQueuedCellSave";

export type { RazmerCopySource };
// Материјално — organising view over the stage's weighed materials, NOT a data-entry table.
// Шифра/Назив/ЕМ/Количина come from Размерување or Меѓу/Полупроизвод (both seeded by OS.tsx —
// the latter into the stage it is ISSUED to, which is the stage that consumes it) and are read-only here;
// rows can be neither typed nor deleted. Editable: only the Постапка/Инструкција text.
// The Постапка's Реден бр. is now READ-ONLY too — it is DERIVED from Фаза (OS.tsx's
// `regroupedMaterialnoGroups`), not typed here, so there is nothing left to sort-on-blur.
// Each row's "РБ" column is also read-only — it mirrors the parent group's `priority`.
// Постапка + Инструкција are merged (rowspan) over the group's rows. Keyboard nav is
// group-aware (flat-array useSpreadsheetKeyboard can't express groups), hand-rolled via a
// `${gi}:${ii}:${col}` ref map.

export interface MatItem {
  accountingTypeCode?: string;
  /** Item's accounting-type group (raw-material/packaging/intermediate/semi-finished/
   *  finished-product/cost), as returned by the server's accountingType populate. RN
   *  work-order-role gating reads this — never the raw code — via
   *  utils/accountingTypeGroup's isMaterial/isIntermediateOrSemi/isFinishedProduct. */
  accountingTypeGroup?: string;
  osRowId?: string;
  itemId: string;
  ident: string;
  name: string;
  unitCode: string;   // ЕМ (from item)
  quantity: string;   // Количина составница
  priority: string;   // Приоритет from Размерување — read-only in Материјално
  measuredQty?: string;
  serialNumber?: string;
  expiryDate?: string;
  waste?: string;
  rastur?: string;
  workedByName?: string;
  workedAt?: string;
  workedBy?: string | { _id?: string };
  verifiedByName?: string;
  verifiedAt?: string;
  verifiedBy?: string | { _id?: string };
  verifyRequested?: boolean;
  confirmed?: boolean;
  receiptDerived?: boolean;
  // NOTE: no Реден број field. The number shown beside Шифра IS the parent group's
  // `priority` — it says which Постапка the row belongs to, so duplicates are normal
  // (every row of Постапка 1 shows 1). Plain read-only display — no move control.
}
export interface MatGroup {
  procedure: string;    // Постапка (merged)
  instruction: string;  // Инструкција (merged)
  items: MatItem[];
  // Реден број of the Постапка — same sort-key semantics as MatItem.priority.
  priority: string;
  // Hidden data field — which Subprocess (left stage tree) this group belongs to.
  // Not rendered as a column; stamped automatically by OS.tsx from activeStage.
  subprocessId: string;
  // true = user-inserted instruction-only Постапка (Shift+↑), never receives items — the
  // discriminator that stops a phase group which lost all its materials from being mistaken
  // for one.
  manual: boolean;
}

export const emptyItem = (): MatItem => ({
  osRowId: "", itemId: "", ident: "", name: "", unitCode: "", quantity: "", priority: "",
  measuredQty: "", serialNumber: "", expiryDate: "", waste: "", rastur: "",
  workedByName: "", workedAt: "", verifiedByName: "", verifiedAt: "", confirmed: false,
});
// A fresh Постапка starts with NO rows — rows only ever arrive from Размерување, so seeding
// an empty one would render a blank line that can be neither filled in nor deleted.
export const emptyGroup = (n = 0, priority = "", manual = false): MatGroup => ({ procedure: "", instruction: "", items: Array.from({ length: n }, emptyItem), subprocessId: "", priority, manual });

// Kept ONLY because RNWorkOrder.tsx (the RN twin screen, outside this task's scope) still
// imports and calls this for its own materialno seeding — this screen (OS.tsx) no longer
// calls it, since Реden бр. here is derived from Фаза instead. Do not remove without also
// updating RNWorkOrder.tsx.
export const nextGroupPriority = (groups: MatGroup[]): string => String(groups.length + 1);

// Caret-line detection for merged textareas: native caret move vs jump to next/prev group.
const isLastLine = (ta: HTMLTextAreaElement): boolean =>
  ta.selectionStart === ta.selectionEnd && ta.value.indexOf("\n", ta.selectionStart) === -1;
const isFirstLine = (ta: HTMLTextAreaElement): boolean =>
  ta.selectionStart === ta.selectionEnd && ta.value.lastIndexOf("\n", ta.selectionStart - 1) === -1;

const TH: React.FC<{ w?: number; title?: string; fixed?: boolean; children: React.ReactNode }> = ({
  w, title, fixed, children,
}) => (
  <th
    className={OS_TH_CLS}
    style={w === undefined ? undefined : fixed ? { minWidth: w, width: w } : { width: w }}
    title={title}
  >
    {children}
  </th>
);

const mergedCls = "border border-gray-300 align-top p-1";

interface Props {
  groups: MatGroup[];
  onChange: (groups: MatGroup[]) => void;
  onChangeAndSave?: (groups: MatGroup[]) => void;
  mode?: "os" | "rn";
  isSaving?: boolean;
  /** RN: allow Copy only after the matching Размерување section was dispatched. */
  allowCopyFromRazmer?: boolean;
  /** RN: itemId → Количина размерена + Серија + Рок from Размерување. */
  razmerCopySourceByItemId?: Record<string, RazmerCopySource>;
  /** RN: extra toolbar actions rendered next to Копирај (e.g. Издај материјал). */
  leadingActions?: React.ReactNode;
  lockUnsavedRows?: boolean;
}

const entityIdOf = (value?: string | { _id?: string } | null): string => {
  if (!value) return "";
  return typeof value === "object" ? (value._id || "") : value;
};

const formatDate = (value?: string): string => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("DD.MM.YYYY") : value;
};

const OSMaterialnoTable: React.FC<Props> = ({
  groups,
  onChange,
  onChangeAndSave,
  mode = "os",
  isSaving,
  allowCopyFromRazmer = false,
  razmerCopySourceByItemId = {},
  leadingActions,
  lockUnsavedRows = false,
}) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const isRn = mode === "rn";
  const { user } = useContext(AuthContext);
  const currentUserId = user?._id || "";
  const setGroups = (u: React.SetStateAction<MatGroup[]>) =>
    onChange(typeof u === "function" ? (u as (p: MatGroup[]) => MatGroup[])(groups) : u);
  const saveCurrentGroups = useCallback((updated: MatGroup[]) => {
    if (onChangeAndSave) onChangeAndSave(updated);
    else onChange(updated);
  }, [onChange, onChangeAndSave]);
  const { saveOrQueue, isQueued } = useRNQueuedCellSave({
    value: groups,
    isSaving,
    scopeKey: groups
      .flatMap((group) => group.items.map((item) => item.osRowId || ''))
      .sort()
      .join(','),
    onSave: saveCurrentGroups,
    onDiscard: () => showToast(t('os.materialno.queuedCellSaveDiscarded'), 'info'),
  });
  const cellRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  const [selectedGroups, setSelectedGroups] = useState<Set<number>>(new Set());
  const isSomeSelected = selectedGroups.size > 0;

  // ─── View sort (presentation only — sorts the GROUPS, item order inside a group is
  // untouched) ──────────────────────────────────────────────────────────────────────────
  const getMaterialnoValue = useCallback((g: MatGroup, field: string): string | number => {
    switch (field) {
      case "priority": {
        const n = parseFloat((g.priority || "").trim());
        return Number.isFinite(n) ? n : 0;
      }
      case "procedure":
        return g.procedure || "";
      case "instruction":
        return g.instruction || "";
      default:
        return "";
    }
  }, []);

  const { sortField, sortDirection, handleSort, resetSort, isSorted, sortedItems } =
    useTableViewSort<MatGroup>(groups, getMaterialnoValue);

  // "РБ" is a plain 1,2,3... counter over materials in NATURAL order — computed once here
  // (as a gi:ii lookup) so sorting the groups never renumbers it. Replaces the old inline
  // `let itemRowCounter; {++itemRowCounter}` which incremented in DISPLAY order.
  const rowCounterMap = useMemo(() => {
    const map = new Map<string, number>();
    let counter = 0;
    groups.forEach((g, gi) => {
      g.items.forEach((_, ii) => {
        counter += 1;
        map.set(`${gi}:${ii}`, counter);
      });
    });
    return map;
  }, [groups]);

  // ─── Search (marks + jumps — never filters, never reorders) ────────────────────────────
  const searchCells = useMemo<SearchCell[]>(() => {
    const cells: SearchCell[] = [];
    sortedItems.forEach(({ item: g, index: gi }) => {
      if (g.procedure) cells.push({ id: `${gi}:procedure`, text: g.procedure });
      if (g.instruction) cells.push({ id: `${gi}:instruction`, text: g.instruction });
      g.items.forEach((it, ii) => {
        if (it.ident) cells.push({ id: `${gi}:${ii}:ident`, text: it.ident });
        if (it.name) cells.push({ id: `${gi}:${ii}:name`, text: it.name });
      });
    });
    return cells;
  }, [sortedItems]);

  const {
    searchInput, setSearchInput, searchTerm, handleSearch, clearSearch,
    matchCount, activeIndex, next, prev, registerCell, isMatch, isActive,
  } = useTableSearchHighlight(searchCells);

  const cellHighlightCls = (id: string): string => {
    if (isActive(id)) return " bg-blue-300 dark:bg-blue-700/70 ring-2 ring-blue-500";
    if (isMatch(id)) return " bg-blue-100 dark:bg-blue-900/40";
    return "";
  };

  const toggleGroup = (gi: number) => {
    if (isSorted || isRn) return;
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gi)) next.delete(gi);
      else next.add(gi);
      return next;
    });
  };

  const isAllSelected = groups.length > 0 && selectedGroups.size === groups.length;
  const toggleAllGroups = () => {
    if (isSorted || isRn) return;
    setSelectedGroups(isAllSelected ? new Set() : new Set(groups.map((_, gi) => gi)));
  };

  // Only whole Постапки are deletable. Individual item rows are not: the row set of this
  // table IS the Размерување row set for the stage, so a row is removed by removing its
  // weighing row, never here.
  const deleteSelected = () => {
    if (isSorted || isRn) return;
    const next = groups.filter((_, gi) => !selectedGroups.has(gi));
    // The table must always keep one group: with zero groups there is no cell to type into.
    onChange(next.length > 0 ? next : [emptyGroup(0, "1")]);
    setSelectedGroups(new Set());
    cellRefs.current.clear();
  };

  const regRef = (gi: number, ii: number, col: number) => (el: HTMLElement | null) => {
    cellRefs.current.set(`${gi}:${ii}:${col}`, el);
  };
  const focusCell = (gi: number, ii: number, col: number) => {
    cellRefs.current.get(`${gi}:${ii}:${col}`)?.focus();
  };

  const patchGroup = (gi: number, p: Partial<Pick<MatGroup, "procedure" | "instruction" | "priority">>) => {
    if (isRn) return;
    setGroups((prev) => prev.map((g, idx) => (idx === gi ? { ...g, ...p } : g)));
  };

  const patchActual = (gi: number, ii: number, p: Partial<MatItem>) => {
    if (!isRn) return;
    onChange(groups.map((g, gIdx) => gIdx === gi ? {
      ...g,
      items: g.items.map((it, iIdx) => iIdx === ii ? {
        ...it,
        workedByName: "",
        workedAt: "",
        verifiedByName: "",
        verifiedAt: "",
        verifyRequested: false,
        ...p,
      } : it),
    } : g));
  };

  // Копирај: Количина размерена, Серија и Рок (од Размерување) → Издадена кол. / Серија / Рок.
  // Само по испраќање на соодветната Размерување секција; се полнат само празни клетки (секоја
  // посебно, никогаш не се пребришува внесена вредност). Ставка измерена во повеќе серии
  // (различни Серии) не се допира — се прикажува предупредување да се внесе рачно.
  const fillsEmptyField = (it: MatItem, source?: RazmerCopySource): boolean => {
    if (!source || source.multipleLots) return false;
    return (!!source.qty && !(it.measuredQty || "").trim())
      || (!!source.serialNumber && !(it.serialNumber || "").trim())
      || (!!source.expiryDate && !(it.expiryDate || "").trim());
  };

  const hasCopyableMeasuredQty = isRn && allowCopyFromRazmer && groups.some((g) =>
    g.items.some((it) => {
      const source = razmerCopySourceByItemId[it.itemId];
      return !!source?.multipleLots || fillsEmptyField(it, source);
    })
  );

  const copyQuantityToIssued = () => {
    if (!isRn || isSorted || isSaving || !allowCopyFromRazmer || !hasCopyableMeasuredQty) return;
    const multiLotIdents: string[] = [];
    const updated = groups.map((g) => ({
      ...g,
      items: g.items.map((it) => {
        const source = razmerCopySourceByItemId[it.itemId];
        if (source?.multipleLots) {
          if (it.ident && !multiLotIdents.includes(it.ident)) multiLotIdents.push(it.ident);
          return it;
        }
        if (!source || !fillsEmptyField(it, source)) return it;
        return {
          ...it,
          measuredQty: (it.measuredQty || "").trim() ? it.measuredQty : source.qty,
          serialNumber: (it.serialNumber || "").trim() ? it.serialNumber : source.serialNumber,
          expiryDate: (it.expiryDate || "").trim() ? it.expiryDate : source.expiryDate,
          workedByName: "",
          workedAt: "",
          verifiedByName: "",
          verifiedAt: "",
          verifyRequested: false,
        };
      }),
    }));
    onChange(updated);
    if (multiLotIdents.length > 0) {
      showToast(t("rnMaterialno.copyMultipleLots", { idents: multiLotIdents.join(", ") }), "warning");
    }
  };

  const setVerifyRequested = (rowKey: string, verifyRequested: boolean) => {
    if (!isRn) return;
    saveOrQueue(rowKey, (current) => current.map((g) => ({
      ...g,
      items: g.items.map((it) => it.osRowId === rowKey ? { ...it, verifyRequested } : it),
    })));
  };

  // One second-person verification action for every eligible row in this stage.
  // This deliberately mirrors the per-row "Проверил" condition and therefore
  // preserves the separate operator/verifier audit trail.
  const matVerifiableItems = useMemo(() => {
    if (!isRn) return [];
    const eligible: MatItem[] = [];
    groups.forEach((group) => group.items.forEach((item) => {
      const workerId = entityIdOf(item.workedBy);
      if (
        item.confirmed
        && workerId
        && workerId !== currentUserId
        && (item.measuredQty || item.serialNumber || item.expiryDate || item.waste || item.rastur)
      ) eligible.push(item);
    }));
    return eligible;
  }, [groups, isRn, currentUserId]);
  const allMatVerifiedOrRequested = matVerifiableItems.length > 0
    && matVerifiableItems.every((item) => !!(item.verifiedByName || item.verifiedAt || item.verifyRequested));
  const someMatVerifiedOrRequested = matVerifiableItems.some(
    (item) => !!(item.verifiedByName || item.verifiedAt || item.verifyRequested)
  );
  const hasOpenMatVerification = matVerifiableItems.some((item) => !(item.verifiedByName || item.verifiedAt));

  const toggleAllMatVerification = () => {
    if (!isRn || isSorted || isSaving || !hasOpenMatVerification) return;
    const shouldRequest = !allMatVerifiedOrRequested;
    const updated = groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        const workerId = entityIdOf(item.workedBy);
        const canVerify = !!(
          item.confirmed
          && workerId
          && workerId !== currentUserId
          && (item.measuredQty || item.serialNumber || item.expiryDate || item.waste || item.rastur)
        );
        const isVerified = !!(item.verifiedByName || item.verifiedAt);
        return canVerify && !isVerified ? { ...item, verifyRequested: shouldRequest } : item;
      }),
    }));
    if (onChangeAndSave) onChangeAndSave(updated);
    else onChange(updated);
  };

  // ─── Confirm (RN only) — same two-state semantics as OSRazmerTable's ✓ column:
  // empty (measuredQty "" + confirmed false) or confirmed (measuredQty set + confirmed
  // true). Ticking an empty row copies `it.quantity` (Кол. составница) into measuredQty;
  // ticking a manually-typed row only confirms it. Unticking clears the row back to empty,
  // including workedByName/workedAt/verifiedByName/verifiedAt/verifyRequested — an
  // emptied row must never stay "verified". This ✓ is the ONLY control that moves a row
  // between the two states. Scope is `groups` — the rows this table currently renders for
  // the active stage — never the whole normativ.
  const matEligibleItems = useMemo(() => {
    if (!isRn) return [];
    const eligible: MatItem[] = [];
    groups.forEach((g) => g.items.forEach((it) => {
      if (lockUnsavedRows && !it.osRowId) return;
      if ((it.quantity || "").trim() || (it.measuredQty || "").trim()) eligible.push(it);
    }));
    return eligible;
  }, [groups, isRn, lockUnsavedRows]);

  const allMatConfirmed = matEligibleItems.length > 0 && matEligibleItems.every((it) => it.confirmed);
  const someMatConfirmed = matEligibleItems.some((it) => it.confirmed);

  const setConfirmed = (rowKey: string, checked: boolean) => {
    if (!isRn) return;
    saveOrQueue(rowKey, (current) => current.map((g) => ({
      ...g,
      items: g.items.map((it) => {
        if (it.osRowId !== rowKey) return it;
        if (checked) {
          const hasTyped = !!(it.measuredQty || "").trim();
          return {
            ...it,
            measuredQty: hasTyped ? it.measuredQty : it.quantity,
            confirmed: true,
            workedByName: "",
            workedAt: "",
            verifiedByName: "",
            verifiedAt: "",
            verifyRequested: false,
          };
        }
        return {
          ...it,
          measuredQty: "",
          confirmed: false,
          workedByName: "",
          workedAt: "",
          verifiedByName: "",
          verifiedAt: "",
          verifyRequested: false,
        };
      }),
    })));
  };

  const toggleAllMatConfirmed = () => {
    if (!isRn || isSorted) return;
    const shouldConfirm = !allMatConfirmed;
    const updated = groups.map((g) => ({
      ...g,
      items: g.items.map((it) => {
        const eligible = !!(it.quantity || "").trim() || !!(it.measuredQty || "").trim();
        if (!eligible) return it;
        if (shouldConfirm) {
          // Already-confirmed rows are untouched — a second person's Проверил signature
          // must never be blanked by ticking the master box for the one row still open.
          if (it.confirmed) return it;
          const hasTyped = !!(it.measuredQty || "").trim();
          return {
            ...it,
            measuredQty: hasTyped ? it.measuredQty : it.quantity,
            confirmed: true,
            workedByName: "",
            workedAt: "",
            verifiedByName: "",
            verifiedAt: "",
            verifyRequested: false,
          };
        }
        return {
          ...it,
          measuredQty: "",
          confirmed: false,
          workedByName: "",
          workedAt: "",
          verifiedByName: "",
          verifiedAt: "",
          verifyRequested: false,
        };
      }),
    }));
    if (onChangeAndSave) onChangeAndSave(updated);
    else onChange(updated);
  };

  // The group's Реден број is a merged cell like the two textareas, and read-only (derived
  // from Фаза by OS.tsx), but it is still a focusable stop in the keyboard-nav map, so it
  // gets its own handler instead of the caret-line textarea one.
  const handleGroupPriorityKeyDown = (e: React.KeyboardEvent, gi: number) => {
    if (isSorted || isRn) return;
    if (e.key === "ArrowUp" && e.shiftKey) {
      e.preventDefault();
      const keyOf = (g: MatGroup): number | null => {
        const t = (g.priority || "").trim();
        if (t === "") return null; // trailing group — Number("") is 0, must special-case before parsing
        const n = Number(t);
        return Number.isFinite(n) ? n : null;
      };
      const target = groups[gi];
      const targetKey = keyOf(target);
      let newKey: number;
      if (targetKey === null) {
        // target is the trailing (blank-priority) group — insert after every numbered key
        const finiteKeys = groups.map(keyOf).filter((k): k is number => k !== null);
        newKey = finiteKeys.length > 0 ? Math.max(...finiteKeys) + 0.5 : 0.5;
      } else if (gi === 0) {
        newKey = targetKey - 0.5;
      } else {
        const prevKey = keyOf(groups[gi - 1]);
        newKey = prevKey !== null ? (prevKey + targetKey) / 2 : targetKey - 0.5;
      }
      const newGroup: MatGroup = {
        procedure: "",
        instruction: "",
        items: [],
        subprocessId: target.subprocessId,
        priority: String(newKey),
        manual: true,
      };
      const next = [...groups.slice(0, gi), newGroup, ...groups.slice(gi)];
      setGroups(next);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (gi < groups.length - 1) focusCell(gi + 1, 0, 6);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (gi > 0) focusCell(gi - 1, 0, 6);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusCell(gi, 0, 0);
    }
  };

  // Darkened "plan" shade for RN — OS recipe columns stay locked, while RN actual
  // columns remain operator-owned cells saved to RNWorkOrder.materialnoActuals.
  const planCellCls = isRn ? " bg-slate-50 cursor-default" : ""; // light tint = pulled from the normativ (2026-10-09), not a dark disabled block
  const actualInputCls = `w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_CLS}`;
  const readOnlyActualInputCls = "w-full h-6 px-1 border-0 outline-none text-[11px] bg-gray-50 cursor-not-allowed";
  const priorityDivCls = isRn
    ? "w-full h-full min-h-[72px] text-center text-[11px] text-gray-700 cursor-not-allowed"
    : "w-full h-full min-h-[72px] text-center text-[11px] text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50";
  const textareaCls = isRn
    ? "w-full h-full min-h-[72px] resize-none border-0 outline-none text-[11px] bg-transparent text-gray-600 cursor-default"
    : `w-full h-full min-h-[72px] resize-none border-0 outline-none text-[11px] bg-transparent ${OS_PLAIN_DISABLED_CLS}`;

  // Group-selection checkbox + the rowSpan'd Постапка/Инструкција textareas. Rendered on
  // whichever row is FIRST in the group — a mirrored Размерување row when there is one,
  // otherwise the first editable row.
  const renderMergedCells = (g: MatGroup, gi: number, span: number) => (
    <>
      <td className="border border-gray-300 align-top text-center px-0 py-1" rowSpan={span}>
        <input
          type="checkbox"
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          checked={selectedGroups.has(gi)}
          onChange={() => toggleGroup(gi)}
          onClick={(e) => e.stopPropagation()}
          disabled={isSorted || isRn}
        />
      </td>
      <td className="border border-gray-300" rowSpan={span} />
      <td className={`border border-gray-300 align-top px-1 py-1${planCellCls}`} rowSpan={span}>
        {/* This div must fill the whole merged cell: it is the Shift+↑ keyboard target
            (handleGroupPriorityKeyDown), and a manual group renders no text at all, so a
            text-sized target would be unclickable on exactly the rows it created. */}
        <div
          ref={isSorted || isRn ? undefined : regRef(gi, 0, 6)}
          tabIndex={isSorted || isRn ? -1 : 0}
          className={priorityDivCls}
          onKeyDown={(e) => handleGroupPriorityKeyDown(e, gi)}
        >
          {g.manual ? '' : g.priority}
        </div>
      </td>
      <td
        ref={registerCell(`${gi}:procedure`)}
        className={`${mergedCls}${planCellCls}${cellHighlightCls(`${gi}:procedure`)}`}
        rowSpan={span}
      >
        <textarea
          ref={isSorted || isRn ? undefined : regRef(gi, 0, 0)}
          className={textareaCls}
          value={g.procedure}
          readOnly={isSorted || isRn}
          disabled={isSorted || isRn}
          onChange={(e) => patchGroup(gi, { procedure: e.target.value })}
          onKeyDown={(e) => handleTextareaKeyDown(e, gi, 0)}
        />
      </td>
      <td
        ref={registerCell(`${gi}:instruction`)}
        className={`${mergedCls}${planCellCls}${cellHighlightCls(`${gi}:instruction`)}`}
        rowSpan={span}
      >
        <textarea
          ref={isSorted || isRn ? undefined : regRef(gi, 0, 1)}
          className={textareaCls}
          value={g.instruction}
          readOnly={isSorted || isRn}
          disabled={isSorted || isRn}
          onChange={(e) => patchGroup(gi, { instruction: e.target.value })}
          onKeyDown={(e) => handleTextareaKeyDown(e, gi, 1)}
        />
      </td>
    </>
  );

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, gi: number, col: number) => {
    if (isSorted) return;
    const ta = e.currentTarget;
    if (e.key === "ArrowDown") {
      if (!isLastLine(ta)) return;
      e.preventDefault();
      if (gi < groups.length - 1) {
        focusCell(gi + 1, 0, col);
      }
      return;
    }
    if (e.key === "ArrowUp") {
      if (!isFirstLine(ta)) return;
      e.preventDefault();
      if (gi > 0) focusCell(gi - 1, 0, col);
    }
  };

  return (
    <OSCard
      title={isRn ? "Материјално издавање" : "Материјално"}
      notice={isSorted && <OSCardNotice>{t("tableTools.sortLockNotice")}</OSCardNotice>}
      actions={(
        <>
          <TableViewToolbar
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            searchTerm={searchTerm}
            handleSearch={handleSearch}
            clearSearch={clearSearch}
            matchCount={matchCount}
            activeIndex={activeIndex}
            next={next}
            prev={prev}
            sortField={sortField}
            sortDirection={sortDirection}
            isSorted={isSorted}
            resetSort={resetSort}
            t={t}
            leadingActions={isRn ? (
              <>
                <button
                  type="button"
                  onClick={copyQuantityToIssued}
                  disabled={isSaving || isSorted || !hasCopyableMeasuredQty}
                  className={`group relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 text-[11px] font-semibold bg-white ${
                    isSaving || isSorted || !hasCopyableMeasuredQty
                      ? "border border-gray-300 text-gray-400 cursor-not-allowed"
                      : "border-2 border-blue-600 text-blue-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105 focus:ring-blue-500/50"
                  }`}
                  title={
                    !allowCopyFromRazmer
                      ? t("rnMaterialno.copyTitleSendFirst")
                      : hasCopyableMeasuredQty
                        ? t("rnMaterialno.copyTitleReady")
                        : t("rnMaterialno.copyTitleNothing")
                  }
                  aria-label={t("rnMaterialno.copyAria")}
                >
                  <Copy size={14} strokeWidth={2.5} />
                  {t("rnMaterialno.copyLabel")}
                </button>
                {leadingActions}
              </>
            ) : undefined}
          />
        </>
      )}
    >
      <div className="overflow-x-auto">
        <table className={`border-collapse w-full table-fixed ${isRn ? "min-w-[1780px]" : "min-w-[1170px]"}`}>
          <thead>
            <tr>
              {/* Group-selection column (merged per Постапка in the body) + delete column —
                  same pair as SimpleSpreadsheetHeader. */}
              <th className="border border-gray-300 bg-gray-200 w-8 h-[26px] px-1">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  checked={isAllSelected}
                  ref={(el) => { if (el) el.indeterminate = isSomeSelected && !isAllSelected; }}
                  onChange={toggleAllGroups}
                  disabled={isSorted || isRn}
                />
              </th>
              <th className="border border-gray-300 bg-gray-200 w-6 h-[26px] px-1">
                {isRn ? null : (
                  <button
                    onClick={deleteSelected}
                    disabled={!isSomeSelected || isSorted}
                    className={`${isSomeSelected && !isSorted ? "text-red-500 hover:text-red-700 cursor-pointer" : "text-gray-300 cursor-not-allowed"} transition-colors`}
                    title={isSomeSelected ? `Избриши ${selectedGroups.size} избрани Постапки` : "Нема избрани Постапки"}
                  >
                    <Trash2 size={14} className="mx-auto" />
                  </button>
                )}
              </th>
              <TableSortTh disabled w={70} field="priority" sortField={sortField} sortDirection={sortDirection} onSort={handleSort}>
                Реден бр.
              </TableSortTh>
              <TableSortTh
                disabled
                w={isRn ? 220 : 330}
                field="procedure"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Постапка
              </TableSortTh>
              <TableSortTh
                disabled
                w={isRn ? 220 : 330}
                field="instruction"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Инструкција
              </TableSortTh>
              {/* Read-only: чист 1,2,3... бројач на редовите со материјали, во приказен
                  редослед. Не е групниот "Реден бр." (тој е одделна колона пред Постапка)
                  и не се уредува тука. */}
              <TH w={isRn ? 40 : 70} fixed={isRn} title={isRn ? "Реден број" : undefined}>РБ</TH>
              {/* Истото поле како во Размерување (`priority`, read-only тука) — насловот е
                  усогласен со таму. Во базата и во Excel останува „Приоритет". */}
              <TH w={isRn ? 60 : 80} fixed={isRn}>Фаза</TH>
              <TH w={90}>Шифра</TH>
              <TH w={240}>Назив</TH>
              <TH w={60}>ЕМ</TH>
              <TH w={60} title="Количина составница">Кол. сос.</TH>
              {isRn && (
                <>
                  <TH w={34}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      checked={allMatConfirmed}
                      ref={(el) => { if (el) el.indeterminate = someMatConfirmed && !allMatConfirmed; }}
                      onChange={toggleAllMatConfirmed}
                      disabled={isSorted || isSaving}
                      title="Потврди сите"
                    />
                  </TH>
                  <TH w={80} fixed title="Издадена количина">
                    <span className="whitespace-nowrap">Изд. кол.</span>
                  </TH>
                  <TH w={110}>Сериски број</TH>
                  <TH w={90}>Рок</TH>
                  <TH w={60} fixed>Отпад</TH>
                  <TH w={60} fixed>Растур</TH>
                  <TH>Работел</TH>
                  <TH>
                    <label className="flex items-center gap-1.5 cursor-pointer" title="Провери ги сите достапни редови">
                      <input
                        type="checkbox"
                        className="w-4 h-4 flex-shrink-0 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        checked={allMatVerifiedOrRequested}
                        ref={(el) => { if (el) el.indeterminate = someMatVerifiedOrRequested && !allMatVerifiedOrRequested; }}
                        onChange={toggleAllMatVerification}
                        disabled={isSorted || isSaving || !hasOpenMatVerification}
                      />
                      <span>Проверил</span>
                    </label>
                  </TH>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(({ item: g, index: gi }) =>
              // A group with no weighed materials still has to emit one <tr>, otherwise its
              // rowSpan'd Постапка/Инструкција cells vanish with it and the Постапка becomes
              // unreachable. It emits a single spanning note instead of a blank row that
              // looks like data but cannot be filled in or deleted.
              g.items.length === 0 ? (
                <tr key={`${gi}-empty`}>
                  {renderMergedCells(g, gi, 1)}
                  <td colSpan={isRn ? 14 : 6} className={`border border-gray-300 h-[26px] px-2 py-0.5 text-[11px] text-gray-400 italic${planCellCls}`}>
                    Нема размерени материјали за оваа фаза
                  </td>
                </tr>
              ) : (
              g.items.map((it, ii) => {
                const hasNoSavedRecipeRow = lockUnsavedRows && !it.osRowId;
                const unsavedRecipeRowTitle = (i18n.language || 'mk').toLowerCase().startsWith('en')
                  ? 'This row is not saved in the recipe – save the recipe first'
                  : 'Редот не е снимен во нормативот – прво снимете го нормативот';
                const workerId = entityIdOf(it.workedBy);
                const canVerify = !!(
                  it.confirmed
                  && workerId
                  && workerId !== currentUserId
                  && (it.measuredQty || it.serialNumber || it.expiryDate || it.waste || it.rastur)
                );
                const isVerified = !!(it.verifiedByName || it.verifiedAt);
                return (
                  <tr key={`${gi}-${ii}`}>
                    {ii === 0 && renderMergedCells(g, gi, g.items.length)}
                    <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700${planCellCls}`}>
                      {rowCounterMap.get(`${gi}:${ii}`)}
                    </td>
                    <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700${planCellCls}`}>
                      {it.priority}
                    </td>
                    {/* Шифра / Назив / ЕМ / Количина are owned by Размерување — read-only here. */}
                    <td
                      ref={registerCell(`${gi}:${ii}:ident`)}
                      className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700${planCellCls}${cellHighlightCls(`${gi}:${ii}:ident`)}`}
                    >
                      {it.ident}
                    </td>
                    <td
                      ref={registerCell(`${gi}:${ii}:name`)}
                      className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700 truncate${planCellCls}${cellHighlightCls(`${gi}:${ii}:name`)}`}
                      title={it.name}
                    >
                      {it.name}
                    </td>
                    <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700${planCellCls}`}>
                      {it.unitCode}
                    </td>
                    <td className={`border border-gray-300 h-[26px] px-1.5 py-0.5 text-right tabular-nums text-[11px] text-gray-700${planCellCls}`}>
                      {it.quantity}
                    </td>
                    {isRn && (
                      <>
                        <td className="relative border border-gray-300 h-[26px] px-1 py-0.5 text-center">
                          <span title={hasNoSavedRecipeRow ? unsavedRecipeRowTitle : undefined}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            checked={!!it.confirmed}
                            // Ништо да се потврди/копира кога редот нема ни планирана ни
                            // измерена количина.
                            disabled={isSorted || hasNoSavedRecipeRow || !it.osRowId || !((it.quantity || "").trim() || (it.measuredQty || "").trim())}
                            title="Потврди"
                            onChange={(e) => setConfirmed(it.osRowId || '', e.target.checked)}
                          />
                          <RNRazmerCellSaveMark status={isSaving || isQueued(it.osRowId || '') ? "saving" : undefined} />
                          </span>
                        </td>
                        <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            className={`${isSorted || it.receiptDerived ? readOnlyActualInputCls : actualInputCls} text-right tabular-nums`}
                            value={it.measuredQty || ""}
                            readOnly={isSorted || !!it.receiptDerived}
                            disabled={isSorted}
                            onChange={(e) => patchActual(gi, ii, { measuredQty: e.target.value, confirmed: false })}
                          />
                        </td>
                        <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                          <input className={isSorted ? readOnlyActualInputCls : actualInputCls} value={it.serialNumber || ""} readOnly={isSorted} disabled={isSorted} onChange={(e) => patchActual(gi, ii, { serialNumber: e.target.value })} />
                        </td>
                        <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                          <input className={isSorted ? readOnlyActualInputCls : actualInputCls} value={it.expiryDate || ""} readOnly={isSorted} disabled={isSorted} onChange={(e) => patchActual(gi, ii, { expiryDate: e.target.value })} />
                        </td>
                        <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                          <input type="text" inputMode="decimal" className={`${isSorted ? readOnlyActualInputCls : actualInputCls} text-right tabular-nums`} value={it.waste || ""} readOnly={isSorted} disabled={isSorted} onChange={(e) => patchActual(gi, ii, { waste: e.target.value })} />
                        </td>
                        <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                          <input type="text" inputMode="decimal" className={`${isSorted ? readOnlyActualInputCls : actualInputCls} text-right tabular-nums`} value={it.rastur || ""} readOnly={isSorted} disabled={isSorted} onChange={(e) => patchActual(gi, ii, { rastur: e.target.value })} />
                        </td>
                        <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700 bg-gray-50">
                          {[it.workedByName, formatDate(it.workedAt)].filter(Boolean).join(" / ")}
                        </td>
                        <td className="relative border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700 bg-gray-50">
                          <div className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              className="w-4 h-4 flex-shrink-0 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              checked={isVerified || !!it.verifyRequested}
                              disabled={isSorted || !it.osRowId || !canVerify || isVerified}
                              title={isVerified ? "Проверено" : "Провери ред"}
                              onChange={(e) => setVerifyRequested(it.osRowId || '', e.target.checked)}
                            />
                            <RNRazmerCellSaveMark status={isSaving || isQueued(it.osRowId || '') ? "saving" : undefined} />
                            <span className={it.verifyRequested && !isVerified ? "text-blue-700" : ""}>
                              {it.verifiedByName || it.verifiedAt
                                ? [it.verifiedByName, formatDate(it.verifiedAt)].filter(Boolean).join(" / ")
                                : it.verifyRequested
                                  ? "За проверка"
                                  : ""}
                            </span>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
              )
            )}
          </tbody>
        </table>
      </div>
    </OSCard>
  );
};

export default OSMaterialnoTable;
